import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, Plus, FileText, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STAGES = ["new_lead", "contacted", "booked_test_drive", "warm_lead", "offer_sent", "won", "lost"];
const stageLabels = {
    new_lead: "Ny lead", contacted: "Kontaktad", booked_test_drive: "Bokad provkörning",
    warm_lead: "Varm lead", offer_sent: "Offert skickad", won: "Vunnen", lost: "Förlorad",
};

export default function ContactDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState("");
    const [tags, setTags] = useState("");

    const fetchContact = () => {
        api.get(`/contacts/${id}`).then((r) => setData(r.data)).catch(() => navigate("/admin/contacts")).finally(() => setLoading(false));
    };

    useEffect(() => { fetchContact(); }, [id]);

    const updateStage = async (stage) => {
        try {
            await api.put(`/contacts/${id}`, { pipeline_stage: stage });
            toast.success("Steg uppdaterat");
            fetchContact();
        } catch { toast.error("Misslyckades"); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        try {
            await api.post(`/contacts/${id}/notes`, { content: note, type: "note" });
            setNote("");
            toast.success("Anteckning tillagd");
            fetchContact();
        } catch { toast.error("Misslyckades"); }
    };

    const updateTags = async () => {
        const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
        try {
            await api.put(`/contacts/${id}`, { tags: tagList });
            toast.success("Taggar uppdaterade");
            fetchContact();
        } catch { toast.error("Misslyckades"); }
    };

    if (loading) return <div className="p-8 text-neutral-400">Laddar...</div>;
    if (!data) return null;

    const { contact, bookings = [], activities = [], offers = [] } = data;

    return (
        <div className="p-8" data-testid="contact-detail">
            <Button variant="ghost" onClick={() => navigate("/admin/contacts")} className="mb-4" data-testid="back-to-contacts">
                <ArrowLeft size={16} className="mr-2" /> Tillbaka
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div className="lg:col-span-2 space-y-6">
                    <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h1 className="text-2xl font-medium tracking-tight">{contact.name}</h1>
                                    <p className="text-sm text-neutral-500">{contact.email} · {contact.phone}</p>
                                </div>
                                <Select value={contact.pipeline_stage} onValueChange={updateStage}>
                                    <SelectTrigger className="w-48" data-testid="stage-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabels[s]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {contact.tags?.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Input data-testid="tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Taggar (kommaseparerade)" className="flex-1" />
                                <Button variant="outline" size="sm" onClick={updateTags} data-testid="update-tags-btn">Uppdatera taggar</Button>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Källa</span>{contact.source}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Skapad</span>{contact.created_at?.split("T")[0]}</div>
                                <div><span className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Steg</span>{stageLabels[contact.pipeline_stage]}</div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Activity Timeline */}
                    <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm uppercase tracking-wider text-neutral-500">Aktivitet</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2 mb-4">
                                <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lägg till anteckning..." className="flex-1 min-h-[60px]" />
                                <Button onClick={addNote} className="bg-black text-white self-end" data-testid="add-note-btn"><Send size={14} /></Button>
                            </div>
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {activities.map((a) => (
                                    <div key={a.id} className="flex gap-3 p-3 border border-neutral-100 rounded">
                                        <MessageSquare size={14} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm">{a.description}</p>
                                            <p className="text-[10px] text-neutral-400 mt-1">{a.type} · {a.created_at?.split("T")[0]}</p>
                                        </div>
                                    </div>
                                ))}
                                {!activities.length && <p className="text-sm text-neutral-400">Ingen aktivitet ännu</p>}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Bookings */}
                    <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm uppercase tracking-wider text-neutral-500">Bokningar</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {bookings.map((b) => (
                                <div key={b.id} className="p-3 border border-neutral-100 rounded mb-2">
                                    <div className="text-sm font-medium">{b.date}</div>
                                    <div className="text-xs text-neutral-400">Birò {b.model} · {b.status}</div>
                                </div>
                            ))}
                            {!bookings.length && <p className="text-sm text-neutral-400">Inga bokningar</p>}
                        </CardContent>
                    </Card>

                    {/* Offers */}
                    <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm uppercase tracking-wider text-neutral-500">Offerter</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {offers.map((o) => (
                                <div key={o.id} className="p-3 border border-neutral-100 rounded mb-2">
                                    <div className="text-sm font-medium">{o.total?.toLocaleString()} SEK</div>
                                    <div className="text-xs text-neutral-400">{o.status} · {o.created_at?.split("T")[0]}</div>
                                </div>
                            ))}
                            {!offers.length && <p className="text-sm text-neutral-400">Inga offerter</p>}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
